import { supportedPaymentMethods, SupportedPaymentMethodsTag } from "consts/ecommerce";
import { WebRoutes } from "consts/enpoints/web";
import type { OnboardingPaymentMethod, PaymentMethod } from "features/ecommerce/types";
import { putOnboardingsPaymentMethodsSteps } from "features/ecommerce/api";
import { useOnboardingForm, useOnboardingStepData } from "ui/organizms/ECommerce/Onboarding/common";
import useCountriesAvailable from "./useCountriesAvailable";

type UsePaymentMethodsArg = {
    readonly getApplicationModel: () => Map<number, {
        readonly index: number;
        readonly isIncomplete: boolean;
        readonly hasRequestedChange: boolean;
        readonly countable?: boolean;
        readonly layout?: {
            readonly stepContainerSize: string | number;
        };
    }>;
};

export const useOnboardingPaymentMethods = ({ getApplicationModel }: UsePaymentMethodsArg) => {
    const { data } = useOnboardingStepData<OnboardingPaymentMethod>();

    const { getCountriesAvailable } = useCountriesAvailable([
        SupportedPaymentMethodsTag.Bancontact,
        SupportedPaymentMethodsTag.Blik,
        SupportedPaymentMethodsTag.Eps,
        SupportedPaymentMethodsTag.Giropay,
        SupportedPaymentMethodsTag.Ideal,
        SupportedPaymentMethodsTag.Mbway,
        SupportedPaymentMethodsTag.Klarna,
        SupportedPaymentMethodsTag.P24
    ]);

    const withValues = useOnboardingForm({
        save: putOnboardingsPaymentMethodsSteps,
        route: WebRoutes.Onboarding.Merchant,
        getApplicationModel
    });

    const paymentMethods = new Map<SupportedPaymentMethodsTag, PaymentMethod>()
        .set(SupportedPaymentMethodsTag.Bancontact, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Bancontact],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Bancontact)
        })
        .set(SupportedPaymentMethodsTag.Blik, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Blik],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Blik)
        })
        .set(SupportedPaymentMethodsTag.Eps, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Eps],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Eps)
        })
        .set(SupportedPaymentMethodsTag.Giropay, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Giropay],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Giropay)
        })
        .set(SupportedPaymentMethodsTag.Ideal, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Ideal],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Ideal)
        })
        .set(SupportedPaymentMethodsTag.Mbway, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Mbway],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Mbway)
        })
        .set(SupportedPaymentMethodsTag.Klarna, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.Klarna],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.Klarna)
        })
        .set(SupportedPaymentMethodsTag.P24, {
            ...supportedPaymentMethods[SupportedPaymentMethodsTag.P24],
            countriesAvailable: getCountriesAvailable(SupportedPaymentMethodsTag.P24)
        });

    const getPaymentMethods = () => Array.from(paymentMethods.values());

    const fromPaymentMethodsInitialValues = <TInitialValues extends object>(initialValues?: TInitialValues) => ({
        ...initialValues,
        methods: data.methods
            ?.split(',')
            .filter(tag => paymentMethods.has(tag as SupportedPaymentMethodsTag))
    });

    const toRequestPaymentMethods = (methods: Array<string>) => (
        Array.from(new Set(methods))
            .join(',')
    );

    return {
        ...data,
        paymentMethods,
        getPaymentMethods,
        withValues,
        fromPaymentMethodsInitialValues,
        toRequestPaymentMethods
    };
};

export default useOnboardingPaymentMethods;
